import React, { useState, useEffect } from 'react';
import { API_BASE, apiHeaders, apiPost, apiDelete, leseFallback, zeigeApiFehler } from '../lib/api';
import { berechneNote } from '../lib/utils';
import { schuljahrFenster } from '../lib/schuljahr';
import { Schueler, KlasseFachEintrag, FehlzeitenVorjahr, ProfilZiel } from '../types';

export const DATUM_UNKLAR = 'Datum unklar';

interface SchuelerContextValue {
  schuelerListe:          Schueler[];
  patchSchueler:          (id: number, fn: (s: Schueler) => Schueler) => void;
  addSchueler:            (vorname: string, nachname: string, klasse: string, extra?: { email?: string; eltern_email?: string; eltern_telefon?: string; geburtsdatum?: string; geschlecht?: string }) => Promise<boolean | null>;
  deleteSchueler:         (id: number) => Promise<void>;
  entferneSchuelerLokal:  (id: number) => void;
  openProfilId:           number | null;
  openProfilZiel:         ProfilZiel | null;
  setOpenProfilId:        (id: number | null, ziel?: ProfilZiel) => void;
  initialKlasseFilter:    string;
  setInitialKlasseFilter: (k: string) => void;
}

export const SchuelerCtx = React.createContext<SchuelerContextValue | null>(null);

export function useSchueler(): SchuelerContextValue {
  const ctx = React.useContext(SchuelerCtx);
  if (!ctx) throw new Error('useSchueler außerhalb Provider');
  return ctx;
}

interface SchuelerProviderProps {
  children:            React.ReactNode;
  eingeloggt:          boolean;
  openProfilId:        number | null;
  openProfilZiel:      ProfilZiel | null;
  onOpenProfilChange:  (id: number | null, ziel?: ProfilZiel) => void;
}

export function SchuelerProvider({ children, eingeloggt, openProfilId, openProfilZiel, onOpenProfilChange }: SchuelerProviderProps) {
  const [schuelerListe, setSchuelerListe] = useState<Schueler[]>([]);
  const [initialKlasseFilter, setInitialKlasseFilter] = useState('');

  function patchSchueler(id: number, fn: (s: Schueler) => Schueler) {
    setSchuelerListe(prev => prev.map(s => s.id === id ? fn(s) : s));
  }

  // Lokal aus der aktiven Liste entfernen (z.B. nach "Schüler verlässt Klasse") — kein API-Call
  function entferneSchuelerLokal(id: number) {
    setSchuelerListe(prev => prev.filter(s => s.id !== id));
  }

  async function addSchueler(vorname: string, nachname: string, klasse: string, extra?: { email?: string; eltern_email?: string; eltern_telefon?: string; geburtsdatum?: string; geschlecht?: string }): Promise<boolean | null> {
    const name = `${vorname.trim()} ${nachname.trim()}`;
    if (schuelerListe.some(s => s.name.trim().toLowerCase() === name.trim().toLowerCase())) {
      return false;
    }
    try {
      const data = await apiPost<any>('/api/schueler', {
        vorname: vorname.trim(), nachname: nachname.trim(),
        klasse_bezeichnung: klasse.trim(),
        ...(extra ?? {}),
      });
      const ab = `${(vorname[0] ?? '?')}${(nachname[0] ?? '?')}`.toUpperCase();
      const neuer: Schueler = {
        id:        data.id,
        name,
        klasse:    klasse.trim() || '–',
        klasse_id: data.klasse_id ?? 0,
        fehlzeiten: { tage_gesamt: 0, tage_unentschuldigt: 0, stunden_gesamt: 0, stunden_unentschuldigt: 0 },
        note:  berechneNote(3, 3, 3, 3, 3),
        avatar: ab, ka1: 3, ka2: 3, ka3: 3, ka4: 3, muendlich: 3,
        positiv: 0, negativ: 0,
        email:          extra?.email          ?? undefined,
        eltern_email:   extra?.eltern_email   ?? undefined,
        eltern_telefon: extra?.eltern_telefon ?? undefined,
        geburtsdatum:   extra?.geburtsdatum   ?? undefined,
        geschlecht:     extra?.geschlecht     ?? undefined,
      };
      setSchuelerListe(prev => [...prev, neuer]);
      return true;
    } catch {
      return null;
    }
  }

  async function deleteSchueler(id: number): Promise<void> {
    setSchuelerListe(prev => prev.filter(s => s.id !== id));
    try {
      await apiDelete(`/api/schueler/${id}`);
    } catch (err) {
      zeigeApiFehler(err, 'Schüler löschen fehlgeschlagen');
      ladeSchueler(); // optimistisch entfernt → Liste wieder in Server-Stand bringen
    }
  }

  // Schüler + Fehlzeiten von SQLite laden wenn eingeloggt
  function ladeSchueler() {
    if (!eingeloggt) { setSchuelerListe([]); return; }
    const headers = apiHeaders();
    Promise.all([
      fetch(`${API_BASE}/api/schueler`,      { headers }).then(leseFallback<any[]>([], '/api/schueler')),
      fetch(`${API_BASE}/api/fehlzeiten`,    { headers }).then(leseFallback<any[]>([], '/api/fehlzeiten')),
      fetch(`${API_BASE}/api/meine-klassen`, { headers }).then(leseFallback<any[]>([], '/api/meine-klassen')),
    ]).then(([schuelerData, fehlzeitenData, klassenData]) => {
      if (!Array.isArray(schuelerData)) return;

      // Fehlzeiten aggregieren pro Schüler-ID — Kennzahlen nur laufendes Schuljahr
      // (Welle J1); ältere Einträge je Schuljahr unter "Vorjahre" (nichts verschwindet).
      const sj = schuljahrFenster();
      const fzMap: Record<number, { stG: number; stU: number; tgG: number; tgU: number }> = {};
      const vjMap: Record<number, Record<string, FehlzeitenVorjahr>> = {};
      if (Array.isArray(fehlzeitenData)) {
        for (const fz of fehlzeitenData) {
          const sid = fz.schueler_id;
          const entsch = fz.entschuldigt === 1 || fz.entschuldigt === true;
          const datum = String(fz.datum ?? '').slice(0, 10);
          if (datum >= sj.von && datum < sj.bisExkl) {
            if (!fzMap[sid]) fzMap[sid] = { stG: 0, stU: 0, tgG: 0, tgU: 0 };
            fzMap[sid].stG += fz.stunden ?? 0;
            fzMap[sid].stU += entsch ? 0 : (fz.stunden ?? 0);
            fzMap[sid].tgG += fz.tage ?? 0;
            fzMap[sid].tgU += entsch ? 0 : (fz.tage ?? 0);
          } else {
            // Alles außerhalb des laufenden Schuljahrs bleibt sichtbar (nur lesen) —
            // auch Zukunfts-Tippfehler und Nicht-ISO-Daten (z.B. aus KI-Extraktion).
            const label = /^\d{4}-\d{2}-\d{2}$/.test(datum) ? schuljahrFenster(datum).label : DATUM_UNKLAR;
            if (!vjMap[sid]) vjMap[sid] = {};
            if (!vjMap[sid][label]) vjMap[sid][label] = {
              label, stunden_gesamt: 0, stunden_unentschuldigt: 0, tage_gesamt: 0, tage_unentschuldigt: 0, eintraege: [],
            };
            const vj = vjMap[sid][label];
            vj.stunden_gesamt         += fz.stunden ?? 0;
            vj.stunden_unentschuldigt += entsch ? 0 : (fz.stunden ?? 0);
            vj.tage_gesamt            += fz.tage ?? 0;
            vj.tage_unentschuldigt    += entsch ? 0 : (fz.tage ?? 0);
            vj.eintraege.push({ id: fz.id, datum, stunden: fz.stunden ?? null, tage: fz.tage ?? null, entschuldigt: entsch ? 1 : 0, grund: fz.grund ?? null });
          }
        }
      }
      // API liefert ORDER BY datum DESC → eintraege bereits neueste zuerst
      const vorjahreVon = (sid: number): FehlzeitenVorjahr[] =>
        Object.values(vjMap[sid] ?? {})
          .map(v => ({ ...v, tage_gesamt: Math.round(v.tage_gesamt * 10) / 10, tage_unentschuldigt: Math.round(v.tage_unentschuldigt * 10) / 10 }))
          .sort((a, b) => (a.label === DATUM_UNKLAR ? 1 : 0) - (b.label === DATUM_UNKLAR ? 1 : 0) || b.label.localeCompare(a.label));

      const klasseMap = new Map<number, string>();
      if (Array.isArray(klassenData)) {
        for (const k of klassenData as KlasseFachEintrag[]) {
          if (!klasseMap.has(k.klasse_id)) klasseMap.set(k.klasse_id, k.klasse_name);
        }
      }

      setSchuelerListe(schuelerData.map((d: any) => {
        const ab = `${(d.vorname ?? '?')[0]}${(d.nachname ?? '?')[0]}`.toUpperCase();
        const fz = fzMap[d.id] ?? { stG: 0, stU: 0, tgG: 0, tgU: 0 };
        return {
          id:        d.id,
          name:      `${d.vorname} ${d.nachname}`,
          klasse:    klasseMap.get(d.klasse_id) ?? String(d.klasse_id ?? ''),
          klasse_id: d.klasse_id ?? 0,
          fehlzeiten: {
            stunden_gesamt:         fz.stG,
            stunden_unentschuldigt: fz.stU,
            tage_gesamt:            Math.round(fz.tgG * 10) / 10,
            tage_unentschuldigt:    Math.round(fz.tgU * 10) / 10,
          },
          fehlzeiten_vorjahre: vorjahreVon(d.id),
          note:     berechneNote(3, 3, 3, 3, 3),
          avatar:   ab,
          ka1: 3, ka2: 3, ka3: 3, ka4: 3, muendlich: 3,
          positiv: 0, negativ: 0,
          email:          d.email          ?? undefined,
          eltern_email:   d.eltern_email   ?? undefined,
          eltern_telefon: d.eltern_telefon ?? undefined,
          geburtsdatum:   d.geburtsdatum   ?? undefined,
          geschlecht:     d.geschlecht     ?? undefined,
          foto:                d.foto                ?? undefined,
          attestpflicht:       d.attestpflicht       ?? 0,
          attestpflicht_seit:  d.attestpflicht_seit  ?? null,
          attestpflicht_notiz: d.attestpflicht_notiz ?? null,
          attest_offen:        d.attest_offen        ?? 0,
        };
      }));
    }).catch(err => console.error('[Laden]', err));
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { ladeSchueler(); }, [eingeloggt]);

  // Reload-Event (z.B. nach Verlassen/Reaktivieren/Neuanlage aus Mail)
  useEffect(() => {
    const handler = () => ladeSchueler();
    window.addEventListener('lehrerdesk-schueler-reload', handler);
    return () => window.removeEventListener('lehrerdesk-schueler-reload', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eingeloggt]);

  return (
    <SchuelerCtx.Provider value={{
      schuelerListe, patchSchueler, addSchueler, deleteSchueler, entferneSchuelerLokal,
      openProfilId, openProfilZiel, setOpenProfilId: onOpenProfilChange,
      initialKlasseFilter, setInitialKlasseFilter,
    }}>
      {children}
    </SchuelerCtx.Provider>
  );
}
